import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "../services/api";
import { getPendingRequests } from "../services/subscriptionService";
import { Crown, AlertTriangle, Clock } from "lucide-react";

const SubscriptionStatus = () => {
  const [subscription, setSubscription] = useState(null);
  const [usage, setUsage] = useState(null);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchSubscriptionData();
    const handleUsageRefresh = () => fetchSubscriptionData();
    window.addEventListener("subscription-usage-refresh", handleUsageRefresh);
    return () => window.removeEventListener("subscription-usage-refresh", handleUsageRefresh);
  }, []);

  useEffect(() => {
    const handleFocus = () => {
      if (!loading && !subscription) fetchSubscriptionData();
    };
    window.addEventListener("focus", handleFocus);
    return () => window.removeEventListener("focus", handleFocus);
  }, [loading, subscription]);

  const fetchSubscriptionData = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) { setLoading(false); return; }
      const subRes = await api.get("/subscriptions/current");
      setSubscription(subRes.data.success ? subRes.data.subscription : null);
      const usageRes = await api.get("/subscriptions/usage");
      if (usageRes.data.success) setUsage(usageRes.data.usage);
      const pendingRes = await getPendingRequests();
      if (pendingRes.success) setPendingRequests(pendingRes.pendingRequests || []);
    } catch (error) {
      console.error("Error fetching subscription data:", error);
      if (error.response?.status === 401) {
        setSubscription(null); setUsage(null); setPendingRequests([]);
      }
    } finally {
      setLoading(false);
    }
  };

  const fmt = (val, max) =>
    `${val}/${max === -1 ? "∞" : max}`;

  const isExpired = subscription?.currentPeriodEnd
    ? new Date() > new Date(subscription.currentPeriodEnd)
    : false;

  if (loading) {
    return (
      <div className="bg-white border border-gray-200 rounded-xl px-4 py-3 animate-pulse">
        <div className="h-3 bg-gray-200 rounded w-48" />
      </div>
    );
  }

  // No subscription, no pending
  if (!subscription && pendingRequests.length === 0) {
    return (
      <div className="bg-white border border-blue-200 rounded-xl px-4 py-3 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <Crown className="w-4 h-4 text-blue-500 shrink-0" />
          <span>No active subscription</span>
        </div>
        <button
          onClick={() => navigate("/subscription-plans")}
          className="text-xs font-medium text-blue-600 hover:underline shrink-0"
        >
          View Plans
        </button>
      </div>
    );
  }

  // Pending approval
  if (!subscription && pendingRequests.length > 0) {
    const req = pendingRequests[0];
    return (
      <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 flex items-center gap-2 text-sm text-amber-700">
        <Clock className="w-4 h-4 shrink-0" />
        <span>
          <span className="font-medium">{req.subscriptionPlan?.name || "Plan"}</span> request pending admin approval
        </span>
      </div>
    );
  }

  const plan = subscription.subscriptionPlan;

  return (
    <div className={`rounded-xl border px-4 py-3 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm ${isExpired ? "bg-red-50 border-red-200" : "bg-white border-gray-200"}`}>

      {/* Plan name + status */}
      <div className="flex items-center gap-2 shrink-0">
        <Crown className={`w-4 h-4 ${isExpired ? "text-red-500" : "text-blue-500"}`} />
        <span className="font-semibold text-gray-800">{plan.name}</span>
        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${isExpired ? "bg-red-100 text-red-600" : "bg-green-100 text-green-600"}`}>
          {isExpired ? "Expired" : "Active"}
        </span>
      </div>

      {/* Divider */}
      <div className="h-4 w-px bg-gray-200 shrink-0 hidden sm:block" />

      {/* Usage stats */}
      {usage && !isExpired && (
        <div className="flex items-center gap-4 text-gray-500 text-xs">
          <span><span className="font-medium text-gray-700">{fmt(usage.customers, plan.maxCustomers)}</span> customers</span>
          <span><span className="font-medium text-gray-700">{fmt(usage.groups, plan.maxGroups)}</span> groups</span>
          <span><span className="font-medium text-gray-700">{fmt(usage.messagesThisPlan, plan.maxMessages)}</span> messages</span>
        </div>
      )}

      {/* Expired notice */}
      {isExpired && (
        <div className="flex items-center gap-1 text-xs text-red-600">
          <AlertTriangle className="w-3.5 h-3.5" />
          <span>Expired {new Date(subscription.currentPeriodEnd).toLocaleDateString()}</span>
        </div>
      )}

      {/* Manage button — pushed to end */}
      <button
        onClick={() => navigate("/subscription-management")}
        className={`ml-auto text-xs font-medium shrink-0 ${isExpired ? "text-red-600 hover:underline" : "text-gray-500 hover:text-gray-700 hover:underline"}`}
      >
        {isExpired ? "Renew" : "Manage"}
      </button>
    </div>
  );
};

export default SubscriptionStatus;
